import { useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { BusyIcon } from '../../components/loading';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDate } from '../../utils/datetime';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Monthly Summary Repository — "Summary" dialog (ALPACA-LIMS parity).  Picks a
 * Year + Month and (optionally) one or more Categories, then **Retrieve** pulls
 * the COMBINED saved samples across every matching MSR — a read-only aggregate
 * for review / export.  Mirrors the legacy MSRSummaryAction's _Copy detail view.
 * (Merged team package 2026-07-02; conformed to the shared `.ca-btn` /
 * `.list-search` / `useNotify` / `<BusyIcon>` / token standard — CLAUDE.md §18.6.)
 */
interface Props { onClose: () => void }

interface CategoryOption { id: string; name: string }

interface SampleRow {
  sampleType?: string; siteName?: string; date?: string; time?: string; projectId?: string;
  keyMap?: string; address?: string;
  freeChlorine?: string; totalChlorine?: string; monochloramine?: string; freeAmmonia?: string;
  totalColiform?: string; ecoli?: string; nitrite?: string; nitrate?: string;
  disinfectant?: string; collector?: string; comment?: string;
  jobId?: string; sampleId?: string;
}

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const RESULT_COLS: { key: keyof SampleRow; label: string }[] = [
  { key: 'freeChlorine', label: 'FreeChlorine' }, { key: 'totalChlorine', label: 'TotalChlorine' },
  { key: 'monochloramine', label: 'MonoChloramine' }, { key: 'freeAmmonia', label: 'FreeAmmonia' },
  { key: 'totalColiform', label: 'TotalColiform' }, { key: 'ecoli', label: 'E.coli' },
  { key: 'nitrite', label: 'Nitrite' }, { key: 'nitrate', label: 'Nitrate' },
  { key: 'disinfectant', label: 'Disinfectant' }, { key: 'comment', label: 'Comment' },
];

// Shared-grid columns (§9) — matches ALPACA-LIMS MSR Summary column order.
// Category (sampleType) leads since this is the aggregate summary view.
// Sorting is DISABLED so the row order stays the input order (selection +
// XLSX export are index-based; the grid's absolute-index row id must align with selectedIdx).
const GRID_COLS: AppColumn<SampleRow>[] = [
  { field: 'sampleType', header: 'Category',       sortable: false },
  { field: 'projectId',  header: 'Run',            sortable: false },
  { field: 'siteName',   header: 'SiteID',         sortable: false },
  { field: 'keyMap',     header: 'KeyMap',         sortable: false },
  { field: 'address',    header: 'Address',        sortable: false },
  { field: 'date',       header: 'Date',           sortable: false, render: (r) => fmtLocalDate(r.date), text: (r) => fmtLocalDate(r.date) },
  { field: 'time',       header: 'Time',           sortable: false },
  ...RESULT_COLS.map((c): AppColumn<SampleRow> => ({ field: c.key as string, header: c.label, sortable: false })),
  { field: 'collector',  header: 'SampleCollector', sortable: false },
  { field: 'jobId',      header: 'JobID',           sortable: false },
  { field: 'sampleId',   header: 'SampleID',        sortable: false },
];

export function MsrSummaryDialog({ onClose }: Props) {
  const notify = useNotify();
  // Bespoke popup keyboard (standing rule 14): Esc closes this hand-rolled overlay.
  const modalKeys = useModalKeys(true, { onClose });
  const now = new Date();
  const [year, setYear]   = useState(String(now.getFullYear()));
  // Multi-month selection (ticket 4898) — compile several months into one
  // aggregate/export.  0-based month indices; defaults to the current month.
  const [selMonths, setSelMonths] = useState<number[]>([now.getMonth()]);
  const [monthOpen, setMonthOpen] = useState(false);
  const [cats, setCats]   = useState<CategoryOption[]>([]);
  const [selCats, setSelCats] = useState<string[]>([]);
  const [catOpen, setCatOpen] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const [rows, setRows]   = useState<SampleRow[]>([]);
  const [retrieving, setRetrieving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [exportMs, setExportMs] = useState<number | null>(null);
  const [selectedIdx, setSelectedIdx] = useState<Set<number>>(new Set()); // row selection

  const filteredCats = catSearch.trim()
    ? cats.filter((c) => c.name.toLowerCase().includes(catSearch.trim().toLowerCase()))
    : cats;
  const toggleCat = (id: string) =>
    setSelCats((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleMonth = (i: number) =>
    setSelMonths((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]).sort((a, b) => a - b));
  const monthLabel = selMonths.length === 0 ? 'Select month(s)'
    : selMonths.map((i) => MONTHS[i]).join('; ');
  // Show the actual selected category NAMES (joined), not "N selected" (ALPACA parity).
  const catLabel = selCats.length === 0 ? 'All categories'
    : selCats.map((id) => cats.find((c) => c.id === id)?.name).filter(Boolean).join('; ');
  const loadLabel = loadMs == null ? null
    : loadMs < 1000 ? `Loaded in ${Math.round(loadMs)} ms` : `Loaded in ${(loadMs / 1000).toFixed(1)} sec`;
  const exportLabel = exportMs == null ? null
    : exportMs < 1000 ? `Exported in ${Math.round(exportMs)} ms` : `Exported in ${(exportMs / 1000).toFixed(1)} sec`;

  // Export the retrieved combined samples to XLSX (ALPACA "Export To XLSX").
  const exportXlsx = async () => {
    // ALPACA parity: a row must be selected first.
    if (selectedIdx.size === 0) { notify.error('Please select an item to proceed further.'); return; }
    setExporting(true);
    const t0 = performance.now();
    try {
      const XLSX = await import('xlsx');
      const data = rows.filter((_, i) => selectedIdx.has(i)).map((s) => ({
        Category: s.sampleType ?? '', Site: s.siteName ?? '', Date: s.date ? fmtLocalDate(s.date) : '',
        Time: s.time ?? '', ProjectID: s.projectId ?? '', KeyMap: s.keyMap ?? '', Address: s.address ?? '',
        FreeChlorine: s.freeChlorine ?? '', TotalChlorine: s.totalChlorine ?? '', Monochloramine: s.monochloramine ?? '',
        FreeAmmonia: s.freeAmmonia ?? '', TotalColiform: s.totalColiform ?? '', 'E.coli': s.ecoli ?? '',
        Nitrite: s.nitrite ?? '', Nitrate: s.nitrate ?? '', Disinfectant: s.disinfectant ?? '',
        Collector: s.collector ?? '', Comment: s.comment ?? '',
      }));
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'MSR Summary');
      const monthTag = selMonths.map((i) => MONTHS[i]).join('-') || 'Months';
      XLSX.writeFile(wb, `MSR_Summary_${monthTag}_${year}.xlsx`);
      const ms = performance.now() - t0;
      setExportMs(ms);
      notify.success('Exported to XLSX successfully.');
    } catch {
      notify.error('Export failed.');
    } finally { setExporting(false); }
  };

  useEffect(() => {
    let alive = true;
    api.get<{ data?: CategoryOption[] }>('/api/v1/monthly-summary-repository/categories')
      .then((r) => { if (alive) setCats(r.data.data ?? []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const retrieve = async () => {
    if (!year) { notify.warning('Please select year.'); return; }
    if (selMonths.length === 0) { notify.warning('Please select at least one month.'); return; }
    setRetrieving(true);
    const t0 = performance.now();
    try {
      const res = await api.post<{ data?: SampleRow[] }>('/api/v1/monthly-summary-repository/summary',
        { months: selMonths, year, categories: selCats });
      const data = res.data.data ?? [];
      setRows(data);
      setSelectedIdx(new Set());          // clear row selection on a fresh retrieve
      setLoadMs(performance.now() - t0);
      notify.success(data.length ? `${data.length} sample${data.length > 1 ? 's' : ''}.` : 'No samples found.');
    } catch (e) {
      notify.error((e as { response?: { data?: { error?: { message?: string } } } })
        .response?.data?.error?.message ?? 'Retrieve failed.');
    } finally { setRetrieving(false); }
  };

  return (
    <div className="qs-overlay" onMouseDown={onClose}>
      <div className="qs-dialog" onMouseDown={(e) => e.stopPropagation()} style={{ width: 1100, maxWidth: '97vw' }} {...modalKeys}>
        <div className="qs-titlebar">
          <span><i className="ti ti-table" /> Monthly Summary — Summary</span>
          <div className="qs-titlebar-right"><i className="ti ti-x qs-close" title="Close" onClick={onClose} /></div>
        </div>

        <div className="qs-body" style={{ maxHeight: '78vh', overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr', gap: 8, alignItems: 'start', maxWidth: 560 }}>
            <label style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>Year:</label>
            <input className="qs-input" value={year} maxLength={4}
              onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, ''))} style={{ width: '100%' }} />
            <label style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>Month:</label>
            {/* Multi-select months (ticket 4898) — mirrors the Category dropdown
                below so several months compile into one aggregate/export. */}
            <div style={{ position: 'relative' }}>
              <button type="button" className="qs-input" onClick={() => setMonthOpen((o) => !o)}
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: selMonths.length ? 'var(--ai-ink)' : 'var(--ai-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{monthLabel}</span>
                <i className="ti ti-chevron-down" />
              </button>
              {monthOpen && (
                <>
                  <div onMouseDown={() => setMonthOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 11, background: '#fff',
                    border: '1px solid var(--ai-line)', borderRadius: 4, boxShadow: '0 4px 14px rgba(0,0,0,0.15)', maxHeight: 300, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ display: 'flex', gap: 10, padding: '4px 8px', borderBottom: '1px solid var(--ai-line-soft)', fontSize: 11 }}>
                      <button type="button" onClick={() => setSelMonths(MONTHS.map((_, i) => i))}
                        style={{ background: 'none', border: 'none', color: 'var(--ai-blue)', cursor: 'pointer', padding: 0 }}>Select all</button>
                      <button type="button" onClick={() => setSelMonths([])}
                        style={{ background: 'none', border: 'none', color: 'var(--ai-icon-delete, #d24a3a)', cursor: 'pointer', padding: 0 }}>Clear</button>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                      {MONTHS.map((m, i) => (
                        <label key={i} title={m}
                          style={{ display: 'flex', gap: 8, padding: '4px 8px', fontSize: 12, cursor: 'pointer', alignItems: 'center' }}>
                          <input type="checkbox" checked={selMonths.includes(i)} onChange={() => toggleMonth(i)} />
                          <span>{m}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, marginTop: 6 }}>Category:</label>
            <div style={{ position: 'relative' }}>
              <button type="button" className="qs-input" onClick={() => setCatOpen((o) => !o)}
                style={{ width: '100%', textAlign: 'left', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: selCats.length ? 'var(--ai-ink)' : 'var(--ai-muted)' }}>{catLabel}</span>
                <i className="ti ti-chevron-down" />
              </button>
              {catOpen && (
                <>
                  <div onMouseDown={() => setCatOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 11, background: '#fff',
                    border: '1px solid var(--ai-line)', borderRadius: 4, boxShadow: '0 4px 14px rgba(0,0,0,0.15)', maxHeight: 260, display: 'flex', flexDirection: 'column' }}>
                    <div style={{ padding: 6, borderBottom: '1px solid var(--ai-line-soft)' }}>
                      {/* Shared .list-search chrome (BO UI Standard) for the type-to-filter box. */}
                      <div className="list-search" style={{ maxWidth: 'none' }}>
                        <span className="tb-i">🔍</span>
                        <input autoFocus value={catSearch} placeholder="Enter text to search…"
                          onChange={(e) => setCatSearch(e.target.value)} />
                        {catSearch && (
                          <span className="list-search-clear" onClick={() => setCatSearch('')}>×</span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 10, padding: '4px 8px', borderBottom: '1px solid var(--ai-line-soft)', fontSize: 11 }}>
                      <button type="button" onClick={() => setSelCats(filteredCats.map((c) => c.id))}
                        style={{ background: 'none', border: 'none', color: 'var(--ai-blue)', cursor: 'pointer', padding: 0 }}>Select all</button>
                      <button type="button" onClick={() => setSelCats([])}
                        style={{ background: 'none', border: 'none', color: 'var(--ai-icon-delete, #d24a3a)', cursor: 'pointer', padding: 0 }}>Clear</button>
                    </div>
                    <div style={{ overflow: 'auto' }}>
                      {filteredCats.length === 0 && <div style={{ padding: 10, color: 'var(--ai-muted)', fontSize: 12 }}>No matches</div>}
                      {filteredCats.map((c) => (
                        <label key={c.id} title={c.name}
                          style={{ display: 'flex', gap: 8, padding: '4px 8px', fontSize: 12, cursor: 'pointer', alignItems: 'center' }}>
                          <input type="checkbox" checked={selCats.includes(c.id)} onChange={() => toggleCat(c.id)} />
                          <span>{c.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
            <span />
            <span style={{ fontSize: 11, color: 'var(--ai-muted)' }}>Leave Category empty for all categories.</span>
          </div>

          {/* Actions — shared .ca-btn family + prototype glyphs. */}
          <div style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
            <button className="ca-btn ca-btn-solid" onClick={retrieve} disabled={retrieving}>
              {retrieving ? <BusyIcon busy icon="ti-download" /> : <span aria-hidden="true">⬇️</span>} Load
            </button>
            <button className="ca-btn" onClick={() => { setSelCats([]); setRows([]); }} disabled={retrieving}>
              <span className="tb-i tb-i-grey">↩︎</span> Reset
            </button>
            <button className="ca-btn" onClick={exportXlsx} disabled={exporting || rows.length === 0} title="Export To XLSX">
              {exporting ? <BusyIcon busy icon="ti-file-spreadsheet" /> : <span className="tb-i tb-i-teal">📤</span>} Export To XLSX
            </button>
            <span style={{ alignSelf: 'center', fontSize: 12, color: 'var(--ai-muted)' }}>
              {selectedIdx.size > 0 ? `${selectedIdx.size} selected · ` : ''}{rows.length} sample{rows.length === 1 ? '' : 's'}
            </span>
            {loadLabel && (
              <span style={{ alignSelf: 'center', fontSize: 11, color: 'var(--ai-green)', background: 'var(--ai-pill-ok-bg)',
                border: '1px solid var(--ai-line)', borderRadius: 10, padding: '2px 8px' }}>
                <i className="ti ti-clock" /> {loadLabel}
              </span>
            )}
            {exportLabel && (
              <span style={{ alignSelf: 'center', fontSize: 11, color: 'var(--ai-blue)', background: 'var(--ai-pill-info-bg)',
                border: '1px solid var(--ai-line)', borderRadius: 10, padding: '2px 8px' }}>
                <i className="ti ti-file-spreadsheet" /> {exportLabel}
              </span>
            )}
          </div>

          {/* Combined samples grid — shared <AppDataGrid> (§9): standard styling,
              footer + pagination, and the standard-aligned selection checkbox
              column. Sorting is off so index-based selection + export stay exact. */}
          <AppDataGrid<SampleRow>
            rows={rows}
            columns={GRID_COLS}
            selectable
            selectedIds={selectedIdx as Set<string | number>}
            onSelectionChange={(ids) => setSelectedIdx(new Set([...ids].map(Number)))}
            showFilters={false}
            emptyText="No data — pick a Month and Year (and optionally Categories), then click Load."
          />
        </div>

        <div className="qs-footer">
          <div className="qs-footer-actions">
            <button className="ca-btn" onClick={onClose}>
              <span className="tb-i tb-i-grey">↩︎</span> Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
